import {isLoggedIn} from '@github-ui/client-env'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {
  type CreateMilestoneMutationCallback,
  MilestonePickerBase,
  type MilestonePickerBaseProps,
  type MilestonePickerMilestone,
} from '@github-ui/item-picker-milestone'
import {QueryClient, useQueryClient, type QueryPersister} from '@github-ui/react-query'
import {useCallback, useEffect, useRef, useState} from 'react'
import {
  apiGetSearchMilestones,
  buildMilestonePickerSearchQueryKey,
  transformSearchMilestoneResponseToQueryData,
  type MilestonePickerQueryData,
} from './api/api-get-search-milestones'
import {parseGraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {useCreateMilestoneMutation} from './mutations/use-create-milestone-mutation'
import {useAnalytics} from '@github-ui/use-analytics'
import {createMilestonePersister} from './api/milestone-persister'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {milestoneDocumentAdapter} from './api/milestone-document-adapter'

type MilestoneQueryKey = ReturnType<typeof buildMilestonePickerSearchQueryKey>

//
// ItemPicker <== ItemPickerWrapper (internal) <== ItemPickerFetcher (internal) <== MilestonePicker (exported)
//                                                                              <== BulkMilestonePicker (exported)

export async function fetchMilestones(
  queryClient: QueryClient,
  {
    owner,
    repo,
    query,
    count,
    persister,
  }: {
    owner: string
    repo: string
    query: string
    count?: number
    persister?: QueryPersister<MilestonePickerQueryData, MilestoneQueryKey>
  },
): Promise<MilestonePickerQueryData> {
  const response = await queryClient.fetchQuery({
    queryKey: buildMilestonePickerSearchQueryKey({owner, repo, query, count}),
    queryFn: async () => {
      const responseJson = await apiGetSearchMilestones({owner, repo, query, count})
      const responseData = parseGraphQLResponse(responseJson)
      return transformSearchMilestoneResponseToQueryData(responseData.data)
    },
    persister,
  })
  return response
}

export type MilestonePickerProps = Omit<
  MilestonePickerBaseProps,
  | 'repositoryId'
  | 'milestones'
  | 'setIsLoading'
  | 'fetchMilestones'
  | 'onCreate'
  | 'viewerCanCreateMilestones'
  | 'onMilestoneCreated'
> & {
  repo: string
  owner: string
}

function ItemPickerFetcher({repo, owner, ...rest}: MilestonePickerProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<MilestonePickerQueryData | null>(null)
  const queryClient = useQueryClient()
  const {mutate: createMilestoneMutate} = useCreateMilestoneMutation()
  const {sendAnalyticsEvent} = useAnalytics()

  useEffect(() => {
    const fetchInitialMilestones = async () => {
      // It considers the most recently updated milestones, so it passes:
      // orderBy: {field: UPDATED_AT, direction: DESC}, orderByStates: [OPEN, CLOSED]
      try {
        let persister: QueryPersister<MilestonePickerQueryData, MilestoneQueryKey> | undefined
        if (isLoggedIn()) {
          persister = async (queryFn, context, query) => {
            const milestonePersister = createMilestonePersister(sendAnalyticsEvent)
            return milestonePersister(queryFn, context, query)
          }
        }
        const response = await fetchMilestones(queryClient, {owner, repo, query: '', persister})
        setData(response)
        setIsLoading(false)
        setIsError(false)
      } catch {
        setIsError(true)
        setIsLoading(false)
      }
    }
    // This useEffect is run to populate the initial list of milestones to show in the picker.
    // It does not take a query into account and only relies on owner / repo
    // This useEffect has the `fetchInitialMilestones` callback (which has `repo` and `owner`)
    // and `fetchKey` as its keys, so it will only be executed if one of those dependencies changes
    // - not whenever the filter changes.
    fetchInitialMilestones()
  }, [fetchKey, owner, queryClient, repo, sendAnalyticsEvent])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('milestones'),
    anchorElement: anchorProps => rest.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  const fetchMilestonesWithQueryClient = useCallback(
    async (options: {owner: string; repo: string; query: string; count?: number}) => {
      const response = await fetchMilestones(queryClient, options)
      return response.milestones
    },
    [queryClient],
  )

  const onCreate: CreateMilestoneMutationCallback = useCallback(
    (input, {onSuccess, onError}) => {
      createMilestoneMutate(input, {
        onSuccess,
        onError,
      })
    },
    [createMilestoneMutate],
  )

  const onMilestoneCreated = useCallback(
    async (milestone: MilestonePickerMilestone) => {
      const queryKey = buildMilestonePickerSearchQueryKey({owner, repo, query: ''})
      await settleConfirmedOperation({
        queryClient,
        targets: [{queryKey, documentAdapter: milestoneDocumentAdapter}],
        ownerId: data?.repositoryId ?? '',
        operation: {
          type: 'addToConnection',
          connectionPath: ['milestones'],
          node: milestone,
          anchor: {at: 'start'},
        },
      })
      setData(queryClient.getQueryData<MilestonePickerQueryData>(queryKey) ?? null)
      // Searches must be fetched again, not populated with a potentially nonmatching title.
      void queryClient.invalidateQueries({
        queryKey: ['item_picker', 'milestones', {owner, repo}],
        refetchType: 'none',
      })
    },
    [data?.repositoryId, owner, queryClient, repo],
  )

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return (
    <MilestonePickerBase
      repo={repo}
      owner={owner}
      repositoryId={data?.repositoryId ?? ''}
      viewerCanCreateMilestones={data?.viewerCanCreateMilestones === true}
      milestones={data?.milestones ?? []}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      fetchMilestones={fetchMilestonesWithQueryClient}
      onCreate={onCreate}
      onMilestoneCreated={onMilestoneCreated}
      {...rest}
    />
  )
}

export function MilestonePicker({
  repo,
  owner,
  anchorElement,
  readonly,
  activeMilestone,
  noMilestoneItem,
  ...rest
}: MilestonePickerProps) {
  return (
    <LazyItemPicker
      keybindingCommandId="item-pickers:open-milestone"
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      createChild={() => (
        <ItemPickerFetcher
          repo={repo}
          owner={owner}
          readonly={readonly}
          initialFilter={''}
          activeMilestone={activeMilestone}
          triggerOpen
          anchorElement={anchorElement}
          noMilestoneItem={noMilestoneItem}
          {...rest}
        />
      )}
      insidePortal={rest.insidePortal}
    />
  )
}
